import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Pencil, X, Wrench, FileCheck2, Accessibility, Satellite, Download, RefreshCw, Loader2, Link as LinkIcon, MapPin } from "lucide-react";
import { toast } from "sonner";
import { PageShell, PageHeader, ActionButton, ModernTable, StatusBadge } from "@/components/ds";
import { useRealtime } from "@/lib/realtime";

const empty = {
  plate: "", model: "", capacity: 8, driver_id: "", status: "active",
  vehicle_type: "minibus", pmr_seats: 0, zone: "", condition: "ok",
  next_inspection: "", documents_ok: true, navixy_tracker_id: "",
  // Configuration d'exploitation (v10.1)
  primary_driver_id: "", primary_tour_id: "", primary_client_name: "",
  primary_active: true, primary_valid_from: "", primary_valid_to: "",
  primary_notes: "",
};

export default function AdminVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [tours, setTours] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [trackers, setTrackers] = useState([]);
  const [logitrakMode, setLogitrakMode] = useState("");
  const [loadingTrackers, setLoadingTrackers] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    const [v, d, t] = await Promise.all([
      api.get("/vehicles"),
      api.get("/users?role=driver&active_only=true"),
      api.get("/tours").catch(() => ({ data: [] })),
    ]);
    setVehicles(v.data); setDrivers(d.data);
    setTours(t.data?.items || t.data || []);
  }, []);

  const loadTrackers = async () => {
    setLoadingTrackers(true);
    try {
      const { data } = await api.get("/vehicles/navixy-trackers");
      setTrackers(data.trackers || []);
      setLogitrakMode(data.mode);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoadingTrackers(false); }
  };

  useEffect(() => { load(); loadTrackers(); }, [load]);

  // Single source of truth: any assignment change anywhere updates the
  // "Chauffeur" column on Flotte → Bus so the user can't see a stale value.
  useRealtime(["tour_assignments"], {
    "assignment.created":        () => load(),
    "assignment.updated":        () => load(),
    "assignment.deleted":        () => load(),
    "assignment.moved":          () => load(),
    "assignment.driver_changed": () => load(),
    "assignment.upserted":       () => load(),
  });

  const openNew = () => { setEditing("new"); setForm(empty); };
  const openEdit = (v) => { setEditing(v.id); setForm({ ...empty, ...v }); };
  const close = () => setEditing(null);

  // Deep-link: /admin/vehicles?focus_vehicle=<id> opens the edit dialog.
  useEffect(() => {
    if (vehicles.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus_vehicle");
    if (!focusId) return;
    const v = vehicles.find((x) => x.id === focusId);
    if (v) {
      openEdit(v);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [vehicles]);

  // Auto-fill primary_client_name from the selected tour (read-only).
  useEffect(() => {
    if (!form.primary_tour_id) {
      if (form.primary_client_name) setForm((f) => ({ ...f, primary_client_name: "" }));
      return;
    }
    const t = tours.find((x) => x.tour_id === form.primary_tour_id);
    const client = t?.client_name || t?.client || "";
    if (client !== form.primary_client_name) {
      setForm((f) => ({ ...f, primary_client_name: client }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.primary_tour_id, tours]);

  const tourLabel = (tid) => {
    const t = tours.find((x) => x.tour_id === tid);
    if (!t) return null;
    return `${t.service_code || "?"}${t.service_letter || ""} · ${t.day_name || ""}`.trim();
  };
  const driverName = (did) => drivers.find((d) => d.id === did)?.name || null;
  const fmtDate = (isoD) => (isoD ? isoD.split("-").reverse().join(".") : null);
  const editingVehicle = editing && editing !== "new" ? vehicles.find((v) => v.id === editing) : null;

  const submit = (e) => {
    e.preventDefault();
    saveVehicle(false);
  };

  const saveVehicle = async (force) => {
    try {
      const payload = {
        ...form,
        capacity: parseInt(form.capacity, 10),
        pmr_seats: parseInt(form.pmr_seats || 0, 10),
        driver_id: form.driver_id || null,
        primary_driver_id: form.primary_driver_id || null,
        primary_tour_id: form.primary_tour_id || null,
        primary_valid_from: form.primary_valid_from || null,
        primary_valid_to: form.primary_valid_to || null,
        primary_active: !!form.primary_active,
        force,
      };
      if (editing === "new") await api.post("/vehicles", payload);
      else await api.put(`/vehicles/${editing}`, payload);
      toast.success("Véhicule enregistré"); close(); load();
    } catch (err) {
      const d = err.response?.data?.detail;
      // Conflit réel détecté par le backend (assignment_conflicts) → confirmer
      if (err.response?.status === 409 && d?.error === "assignment_overlap" && !force) {
        const ok = window.confirm(`⚠️ ${d.message}\n\nEnregistrer quand même (forcer) ?`);
        if (ok) return saveVehicle(true);
        return;
      }
      toast.error(formatApiErrorDetail(d) || err.message);
    }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer ce véhicule ?")) return;
    await api.delete(`/vehicles/${id}`); load();
  };

  const importLogitrak = async () => {
    if (!confirm("Importer les trackers Logitrak non encore liés comme véhicules LOGIBUS ?")) return;
    setImporting(true);
    try {
      const { data } = await api.post("/vehicles/import-from-navixy");
      toast.success(`${data.created} véhicule(s) créé(s) — ${data.already_linked} déjà liés sur ${data.total_trackers} trackers`);
      load(); loadTrackers();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setImporting(false); }
  };

  return (
    <PageShell testId="admin-vehicles-page">
      <PageHeader
        eyebrow="Flotte"
        title="Véhicules"
        subtitle={`${vehicles.length} véhicule${vehicles.length > 1 ? "s" : ""} · ${vehicles.filter(v => v.navixy_tracker_id && !String(v.navixy_tracker_id).startsWith("sim-")).length} avec traceur GPS lié`}
        actions={
          <>
            <ActionButton
              testId="import-navixy-btn"
              variant="ghost"
              size="lg"
              onClick={importLogitrak}
              disabled={importing || trackers.length === 0}
              loading={importing}
              icon={importing ? undefined : Download}
            >
              Importer Logitrak
            </ActionButton>
            <ActionButton
              testId="add-vehicle-btn"
              variant="primary"
              size="lg"
              icon={Plus}
              onClick={openNew}
            >
              Nouveau véhicule
            </ActionButton>
          </>
        }
      />

      <ModernTable testId="vehicles-table-wrap"
        columns={[
          { key: "plate", label: "Plaque" },
          { key: "model", label: "Modèle / Type" },
          { key: "capacity", label: "Places" },
          { key: "pmr_seats", label: "PMR" },
          { key: "zone", label: "Zone" },
          { key: "driver_id", label: "Chauffeur" },
          { key: "condition", label: "État" },
          { key: "next_inspection", label: "Contrôle" },
          { key: "_actions", label: "Actions", align: "right" },
        ]}
        rows={vehicles}
        emptyMessage="Aucun véhicule."
        renderCell={(v, c) => {
          if (c.key === "plate") return (
            <span className="inline-flex items-center gap-1.5 font-mono font-bold">
              {v.plate}
              {v.navixy_tracker_id && (
                String(v.navixy_tracker_id).startsWith("sim-") ? (
                  <span className="px-1.5 py-0.5 bg-orange-100 text-orange-800 border border-orange-300 text-[9px] font-mono uppercase tracking-widest" title="Donnée de démonstration" data-testid={`sim-tracker-${v.id}`}>DÉMO</span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#FFC60A] border border-zinc-900 text-[9px] font-mono uppercase tracking-widest" title="Traceur GPS Logitrak" data-testid={`linked-navixy-${v.id}`}>
                    <Satellite size={9} /> GPS lié
                  </span>
                )
              )}
            </span>
          );
          if (c.key === "model") return (
            <>
              <div>{v.model}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{v.vehicle_type || "—"}</div>
            </>
          );
          if (c.key === "capacity") return <span className="inline-block border border-zinc-900 px-2 py-0.5 text-xs font-mono font-bold">{v.capacity}</span>;
          if (c.key === "pmr_seats") return v.pmr_seats > 0 ? <span className="inline-flex items-center gap-1 font-mono text-xs"><Accessibility size={12} /> {v.pmr_seats}</span> : <span className="text-zinc-400">—</span>;
          if (c.key === "zone") return <span className="text-xs">{v.zone || "—"}</span>;
          if (c.key === "driver_id") {
            const eff = v.current_driver_name;
            const src = v.current_driver_source;
            const fallback = drivers.find((d) => d.id === v.driver_id)?.name;
            if (eff) {
              return (
                <span className="text-xs">
                  <span className="font-medium">{eff}</span>
                  {src && src !== "vehicle_static" && (
                    <span className="ml-1 text-[9px] uppercase font-mono tracking-wider text-zinc-500" title={src}>
                      ({src.startsWith("tour_assignment") ? "auto" : src === "affectation_gtfs" ? "gtfs" : src})
                    </span>
                  )}
                </span>
              );
            }
            return <span className="text-xs text-zinc-400">{fallback || "—"}</span>;
          }
          if (c.key === "condition") {
            const tone = v.condition === "ok" ? "yellow" : v.condition === "maintenance" ? "warning" : "danger";
            const label = v.condition === "ok" ? "OK" : v.condition === "maintenance" ? (<span className="inline-flex items-center gap-1"><Wrench size={10} /> Maint.</span>) : "HS";
            return <StatusBadge tone={tone} label={label} withDot={false} />;
          }
          if (c.key === "next_inspection") return v.next_inspection ? <span className="inline-flex items-center gap-1 font-mono text-xs"><FileCheck2 size={10} /> {v.next_inspection}</span> : <span className="text-zinc-400">—</span>;
          if (c.key === "_actions") return (
            <div className="flex justify-end gap-1">
              <button data-testid={`edit-vehicle-${v.id}`} onClick={() => openEdit(v)} className="p-1.5 border border-zinc-900 hover:bg-zinc-900 hover:text-white"><Pencil size={12} /></button>
              <button data-testid={`delete-vehicle-${v.id}`} onClick={() => remove(v.id)} className="p-1.5 border border-zinc-900 hover:bg-red-600 hover:text-white hover:border-red-600"><Trash2 size={12} /></button>
            </div>
          );
          return v[c.key];
        }}
      />

      {/* Logitrak trackers (read-only fleet from Logitrak account) */}
      <section className="mt-10" data-testid="navixy-trackers-section">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Logitrak · Compte client {logitrakMode === "real" ? "(temps réel)" : "(simulateur)"}</div>
            <h2 className="font-display font-black uppercase text-2xl tracking-tighter">Trackers Logitrak</h2>
            <p className="text-xs text-zinc-600 mt-1">
              <strong>{trackers.length}</strong> trackers GPS sur votre compte Logitrak ·
              <strong className="ml-1">{trackers.filter((t) => t.linked_vehicle).length}</strong> déjà liés à un véhicule LOGIBUS.
            </p>
          </div>
          <ActionButton onClick={loadTrackers} loading={loadingTrackers} icon={loadingTrackers ? undefined : RefreshCw} size="sm" testId="refresh-navixy">
            Rafraîchir
          </ActionButton>
        </div>
        <div className="border border-zinc-900 overflow-x-auto">
          <table className="w-full text-sm" data-testid="navixy-trackers-table">
            <thead className="bg-zinc-900 text-white">
              <tr>
                {["Traceur", "ID technique", "Position", "État", "Vitesse", "Véhicule LOGIBUS"].map((h) => (
                  <th key={h} className="text-left px-4 py-2 text-[10px] font-mono tracking-widest uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trackers.length === 0 && !loadingTrackers && (
                <tr><td colSpan={6} className="p-6 text-center text-zinc-500 text-xs">
                  Aucun tracker — Logitrak hash non configuré ? Allez sur <strong>Suivi live → Paramètres</strong>.
                </td></tr>
              )}
              {trackers.map((t) => (
                <tr key={t.tracker_id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`navixy-row-${t.tracker_id}`}>
                  <td className="px-4 py-2 font-semibold text-xs">
                    {t.label || "Traceur GPS"}
                    {String(t.tracker_id).startsWith("sim-") ? (
                      <span className="ml-1 px-1 py-0.5 text-[8px] uppercase tracking-widest bg-orange-100 text-orange-800 border border-orange-300">DÉMO</span>
                    ) : (
                      <span className="ml-1 px-1 py-0.5 text-[8px] uppercase tracking-widest bg-green-100 text-green-800 border border-green-300">GPS</span>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-[10px] text-zinc-400" title="Identifiant technique Logitrak">
                    #{t.tracker_id}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {t.lat !== null && t.lat !== undefined ? (
                      <span className="inline-flex items-center gap-1"><MapPin size={11} className="text-zinc-500" />{t.lat?.toFixed(4)}, {t.lon?.toFixed(4)}</span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${t.movement_status === "moving" ? "bg-green-500 text-white" : (t.movement_status === "stopped" || t.movement_status === "parked") ? "bg-zinc-200" : "bg-zinc-100"}`}>
                      {t.movement_status === "moving" ? "En mouvement" : t.movement_status === "stopped" ? "Arrêté" : t.movement_status === "parked" ? "Stationné" : t.movement_status === "idle" ? "Inactif" : (t.movement_status || "—")}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{t.speed != null ? `${t.speed} km/h` : "—"}</td>
                  <td className="px-4 py-2">
                    {t.linked_vehicle ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FFC60A] border border-zinc-900 text-[10px] font-mono uppercase tracking-widest" data-testid={`linked-${t.tracker_id}`}>
                        <LinkIcon size={10} /> {t.linked_vehicle.plate}
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Non lié</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="vehicle-modal">
          <form onSubmit={submit} className="bg-white border border-zinc-900 w-full max-w-xl my-8">
            <div className="flex items-center justify-between border-b border-zinc-900 p-4">
              <div className="font-display font-bold text-xl">{editing === "new" ? "Nouveau véhicule" : "Modifier"}</div>
              <button type="button" onClick={close}><X size={18} /></button>
            </div>
            <div className="p-4 grid sm:grid-cols-2 gap-3">
              <div><label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Plaque</label><input data-testid="vehicle-form-plate" required value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} className="mt-1 w-full border border-zinc-900 px-3 py-2" /></div>
              <div><label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Modèle</label><input data-testid="vehicle-form-model" required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-1 w-full border border-zinc-900 px-3 py-2" /></div>
              <div><label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Type</label>
                <select data-testid="vehicle-form-type" value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} className="mt-1 w-full border border-zinc-900 px-3 py-2 bg-white">
                  <option value="van">Van (≤9)</option><option value="minibus">Minibus (10-22)</option><option value="bus">Bus (23+)</option>
                </select>
              </div>
              <div><label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Capacité</label><input data-testid="vehicle-form-capacity" type="number" min={1} max={100} required value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="mt-1 w-full border border-zinc-900 px-3 py-2" /></div>
              <div><label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Places PMR</label><input data-testid="vehicle-form-pmr" type="number" min={0} value={form.pmr_seats} onChange={(e) => setForm({ ...form, pmr_seats: e.target.value })} className="mt-1 w-full border border-zinc-900 px-3 py-2" /></div>
              <div><label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Zone d&apos;exploitation</label><input data-testid="vehicle-form-zone" value={form.zone || ""} onChange={(e) => setForm({ ...form, zone: e.target.value })} className="mt-1 w-full border border-zinc-900 px-3 py-2" /></div>
              <div><label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">État</label>
                <select data-testid="vehicle-form-condition" value={form.condition || "ok"} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="mt-1 w-full border border-zinc-900 px-3 py-2 bg-white">
                  <option value="ok">OK</option><option value="maintenance">Maintenance</option><option value="out_of_service">Hors service</option>
                </select>
              </div>
              <div><label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Prochain contrôle</label><input data-testid="vehicle-form-inspection" type="date" value={form.next_inspection || ""} onChange={(e) => setForm({ ...form, next_inspection: e.target.value })} className="mt-1 w-full border border-zinc-900 px-3 py-2" /></div>
              <div className="flex items-end gap-2"><label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={form.documents_ok} onChange={(e) => setForm({ ...form, documents_ok: e.target.checked })} /> Documents administratifs OK</label></div>
            </div>

            {/* ── Affectation principale (source : tour_assignments) ────── */}
            <div className="border-t-2 border-[#FFC60A] bg-[#FFC60A]/10 px-4 py-2 flex items-center gap-2"
                  data-testid="vehicle-form-primary-section-header">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-zinc-900 text-[#FFC60A] text-[10px] font-mono font-black">★</span>
              <div className="flex-1">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-700 font-bold">Affectation principale</div>
                <div className="text-[11px] text-zinc-600">
                  Définit l&apos;affectation habituelle de ce véhicule. Les remplacements
                  ponctuels sont gérés via les exceptions.
                </div>
              </div>
              <label className="inline-flex items-center gap-1.5 text-xs font-bold">
                <input
                  type="checkbox"
                  checked={form.primary_active !== false}
                  onChange={(e) => setForm({ ...form, primary_active: e.target.checked })}
                  data-testid="vehicle-form-primary-active"
                />
                <span>Active</span>
              </label>
            </div>

            {editingVehicle && (
              <div className="px-4 pt-3 space-y-2">
                {(editingVehicle.primary_tour_id || editingVehicle.primary_driver_id) ? (
                  <div className="border border-zinc-900 bg-white p-3 text-xs space-y-0.5" data-testid="vehicle-primary-summary">
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] font-black">Affectation principale</div>
                    <div>Tournée principale : <strong data-testid="summary-tour">{tourLabel(editingVehicle.primary_tour_id) || "Non définie"}</strong></div>
                    <div>Chauffeur principal : <strong data-testid="summary-driver">{driverName(editingVehicle.primary_driver_id) || "Non affecté"}</strong></div>
                    <div>Validité : <strong data-testid="summary-validity">{fmtDate(editingVehicle.primary_valid_from) || "—"} → {fmtDate(editingVehicle.primary_valid_to) || "sans date de fin"}</strong></div>
                    <div>État : <strong data-testid="summary-state">{editingVehicle.primary_active !== false ? "ACTIVE" : "INACTIVE"}</strong></div>
                  </div>
                ) : (
                  <div className="border border-dashed border-zinc-400 bg-zinc-50 p-3 text-xs" data-testid="vehicle-primary-summary-empty">
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] font-black text-zinc-500">À configurer</div>
                    <div className="text-zinc-500 mt-0.5">Tournée principale : Non définie · Chauffeur principal : Non affecté</div>
                  </div>
                )}
                {editingVehicle.today_exception && (
                  <div className="border border-orange-500 bg-orange-50 p-3 text-xs space-y-0.5" data-testid="vehicle-today-exception">
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] font-black text-orange-700">Exception aujourd&apos;hui</div>
                    <div>Tournée : <strong data-testid="exception-tour">{editingVehicle.today_exception.tour_label || tourLabel(editingVehicle.today_exception.tour_id) || "—"}</strong></div>
                    <div>Bus effectif : <strong data-testid="exception-vehicle">{editingVehicle.today_exception.vehicle_plate || "—"}</strong></div>
                    <div>Chauffeur : <strong data-testid="exception-driver">{editingVehicle.today_exception.driver_name || "Non affecté"}</strong></div>
                    <div>Motif : <strong data-testid="exception-reason">{editingVehicle.today_exception.reason || "—"}</strong></div>
                  </div>
                )}
              </div>
            )}

            <div className="p-4 grid sm:grid-cols-2 gap-3" data-testid="vehicle-form-primary-section">
              <div>
                <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Chauffeur principal</label>
                <select
                  data-testid="vehicle-form-primary-driver"
                  value={form.primary_driver_id || ""}
                  onChange={(e) => setForm({ ...form, primary_driver_id: e.target.value })}
                  className="mt-1 w-full border border-zinc-900 px-3 py-2 bg-white">
                  <option value="">— Non défini —</option>
                  {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Tournée principale</label>
                <select
                  data-testid="vehicle-form-primary-tour"
                  value={form.primary_tour_id || ""}
                  onChange={(e) => setForm({ ...form, primary_tour_id: e.target.value })}
                  className="mt-1 w-full border border-zinc-900 px-3 py-2 bg-white">
                  <option value="">— Non défini —</option>
                  {tours.map((t) => {
                    const lbl = `${t.service_code || "?"}${t.service_letter || ""} · ${t.day_name || ""}`;
                    return <option key={t.tour_id} value={t.tour_id}>{lbl.trim()}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Client (auto)</label>
                <input
                  data-testid="vehicle-form-primary-client"
                  readOnly disabled
                  value={form.primary_client_name || (form.primary_tour_id ? "(client non renseigné sur la tournée)" : "—")}
                  className="mt-1 w-full border border-zinc-300 bg-zinc-50 text-zinc-700 px-3 py-2 cursor-not-allowed" />
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Notes</label>
                <input
                  data-testid="vehicle-form-primary-notes"
                  value={form.primary_notes || ""}
                  onChange={(e) => setForm({ ...form, primary_notes: e.target.value })}
                  placeholder="Affectation année 2026/2027"
                  className="mt-1 w-full border border-zinc-900 px-3 py-2" />
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Valable dès</label>
                <input
                  data-testid="vehicle-form-primary-from"
                  type="date"
                  value={form.primary_valid_from || ""}
                  onChange={(e) => setForm({ ...form, primary_valid_from: e.target.value })}
                  className="mt-1 w-full border border-zinc-900 px-3 py-2" />
              </div>
              <div>
                <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Valable jusqu&apos;au</label>
                <input
                  data-testid="vehicle-form-primary-to"
                  type="date"
                  value={form.primary_valid_to || ""}
                  onChange={(e) => setForm({ ...form, primary_valid_to: e.target.value })}
                  className="mt-1 w-full border border-zinc-900 px-3 py-2" />
              </div>
            </div>

            <div className="border-t border-zinc-900 p-4 flex justify-end gap-2">
              <button type="button" onClick={close} className="px-4 py-2 border border-zinc-900 font-semibold hover:bg-zinc-100">Annuler</button>
              <button data-testid="save-vehicle-btn" type="submit" className="px-4 py-2 bg-[#FFC60A] border border-zinc-900 font-bold hover:bg-[#ffd43a]">Enregistrer</button>
            </div>
          </form>
        </div>
      )}
    </PageShell>
  );
}
